import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, ClipboardList, Settings2, LogOut, Scissors } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LANGS = { en: "EN · English", hi: "HI · हिन्दी", gu: "GU · ગુજરાતી" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-16">
          <button onClick={() => nav("/")} className="flex items-center gap-2 sq-tap" data-testid="brand-home-btn">
            <span className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Scissors size={18} />
            </span>
            <div className="text-left leading-none">
              <div className="font-heading font-bold text-lg tracking-tight">StitchQuote</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Surat · Embroidery</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 rounded-lg" data-testid={TID.langSwitcher}>
                  {lang.toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("langLabel")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={lang} onValueChange={setLang}>
                  {Object.entries(LANGS).map(([k, label]) => (
                    <DropdownMenuRadioItem key={k} value={k} data-testid={`lang-opt-${k}`}>{label}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }} className="h-10" data-testid={TID.logoutBtn}>
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 pt-4 pb-24 animate-fade-in-up">
        {children}
      </main>

      {/* Bottom nav (mobile-first) */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-surface/95 backdrop-blur border-t border-border">
        <div className="max-w-3xl mx-auto grid grid-cols-3">
          <NavTab to="/" icon={<Home size={20} />} label={t("home")} testId={TID.navHome} />
          <NavTab to="/orders" icon={<ClipboardList size={20} />} label={t("orders")} testId={TID.navOrders} />
          <NavTab to="/settings" icon={<Settings2 size={20} />} label={t("settings")} testId={TID.navSettings} />
        </div>
        {user?.business_name && (
          <div className="text-center text-[10px] uppercase tracking-widest text-muted-foreground pb-0.5">
            {user.business_name}
          </div>
        )}
        <div className="text-center text-[9px] text-muted-foreground/60 pb-1 leading-tight">
          Built for embroidery units in Surat, India · Built by Rakesh Solanki
        </div>
      </nav>
    </div>
  );
}

function NavTab({ to, icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testId}
      className={({ isActive }) =>
        `flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${
          isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
